/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '../modals/Modal';
import { Ionicons } from '@expo/vector-icons';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing, ticketColors, ticketFonts, typography } from '../theme/public-api';
import { matchesSearchText } from '../format/public-api';
import { TicketModalHeader } from '../ticket/TicketModalHeader';

export type MasterFilterOption = { id: string; name: string; subtitle?: string; disabled?: boolean };

const SEARCH_OPTIONS_THRESHOLD = 8;

type Props = {
  visible: boolean;
  title: string;
  options: MasterFilterOption[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onClose: () => void;
  allOptionLabel?: string;
  /** Si false, no muestra fila «todas/todos» (p. ej. orden). */
  showAllOption?: boolean;
  /** Ticket nested picker (POS filters clean). */
  appearance?: 'default' | 'ticket';
  footerClearLabel?: string;
  onFooterClear?: () => void;
  footerCreateLabel?: string;
  onFooterCreate?: () => void;
  /**
   * Inline create: pinned "＋ <createLabel>" row that swaps to an input.
   * `onCreateOption` creates the entity; on success the sheet expects the
   * caller to select it (and can close the modal itself).
   */
  createLabel?: string;
  createPlaceholder?: string;
  onCreateOption?: (name: string) => Promise<void>;
};

export function MasterFilterSheetModal({
  visible,
  title,
  options,
  selectedId,
  onSelect,
  onClose,
  allOptionLabel = 'Todas',
  showAllOption = true,
  appearance = 'default',
  footerClearLabel,
  onFooterClear,
  footerCreateLabel,
  onFooterCreate,
  createLabel,
  createPlaceholder,
  onCreateOption,
}: Props) {
  const insets = useSafeAreaInsets();
  const { height: screenHeight, width: screenWidth } = useWindowDimensions();
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const ticket = appearance === 'ticket';
  const wide = screenWidth >= 768;
  const asDialog = ticket && wide;

  useEffect(() => {
    if (!visible) {
      setQuery('');
      setCreating(false);
      setCreateName('');
      setCreateError(null);
    }
  }, [visible]);

  const showSearch = ticket || options.length >= SEARCH_OPTIONS_THRESHOLD;

  const filteredOptions = useMemo(() => {
    if (!query.trim()) return options;
    return options.filter(
      (option) => matchesSearchText(option.name, query) || matchesSearchText(option.subtitle, query),
    );
  }, [options, query]);

  const optionCountHint =
    !ticket && showSearch
      ? query.trim()
        ? `${filteredOptions.length} resultado${filteredOptions.length === 1 ? '' : 's'}`
        : `${options.length} opciones · scrolleá o buscá`
      : ticket && query.trim()
        ? `${filteredOptions.length} resultado${filteredOptions.length === 1 ? '' : 's'}`
        : null;

  const showFooter = !!(footerClearLabel && onFooterClear) || !!(footerCreateLabel && onFooterCreate);

  const selectAndClose = (id: string | null) => {
    onSelect(id);
    onClose();
  };

  const submitCreate = async () => {
    const name = createName.trim();
    if (!name || createBusy || !onCreateOption) return;
    setCreateBusy(true);
    setCreateError(null);
    try {
      await onCreateOption(name);
      setCreating(false);
      setCreateName('');
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : 'No se pudo crear. Reintentá.');
    } finally {
      setCreateBusy(false);
    }
  };

  const searchPlaceholder = ticket ? 'Buscar…' : `Buscar ${title.toLowerCase()}…`;

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View
        style={[
          styles.modalBackdrop,
          ticket && styles.modalBackdropTicket,
          asDialog && styles.modalBackdropDialog,
        ]}
      >
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Cerrar"
        />
        <View
          style={[
            styles.modalSheet,
            ticket && styles.modalSheetTicket,
            asDialog && styles.modalSheetTicketWide,
            {
              paddingBottom: asDialog ? spacing.md : Math.max(insets.bottom, spacing.md),
              maxHeight: screenHeight * (ticket ? 0.62 : 0.78),
            },
          ]}
        >
          {!asDialog ? <View style={[styles.grabber, ticket && styles.grabberTicket]} /> : null}

          {ticket ? (
            <TicketModalHeader title={title} onClose={onClose} compact borderless />
          ) : (
            <View style={styles.modalSheetHeader}>
              <Text style={styles.modalSheetTitle}>{title}</Text>
              <TouchableOpacity
                onPress={onClose}
                style={styles.closeBtn}
                activeOpacity={0.6}
                accessibilityRole="button"
                accessibilityLabel="Cerrar"
              >
                <Ionicons name="close" size={16} color={ticketColors.ink3} />
              </TouchableOpacity>
            </View>
          )}

          {optionCountHint ? (
            <Text style={[styles.optionsHint, ticket && styles.optionsHintTicket]}>{optionCountHint}</Text>
          ) : null}

          {showSearch ? (
            <View style={[styles.searchWrap, ticket && styles.searchWrapTicket]}>
              {!ticket ? (
                <Ionicons name="search" size={18} color={ticketColors.ink3} style={styles.searchIcon} />
              ) : null}
              <TextInput
                style={[styles.searchInput, ticket && styles.searchInputTicket]}
                value={query}
                onChangeText={setQuery}
                placeholder={searchPlaceholder}
                placeholderTextColor={ticket ? ticketColors.ink3 : ticketColors.ink3}
                autoCapitalize="none"
                autoCorrect={false}
                accessibilityLabel={ticket ? 'Buscar' : `Buscar ${title}`}
              />
              {query ? (
                <TouchableOpacity
                  onPress={() => setQuery('')}
                  style={styles.clearSearchBtn}
                  accessibilityLabel="Limpiar búsqueda"
                  accessibilityRole="button"
                >
                  <Ionicons name="close-circle" size={18} color={ticket ? ticketColors.ink2 : ticketColors.ink2} />
                </TouchableOpacity>
              ) : null}
            </View>
          ) : null}

          {onCreateOption && createLabel ? (
            creating ? (
              <View style={styles.createRow}>
                <TextInput
                  style={styles.createInput}
                  value={createName}
                  onChangeText={(text) => {
                    setCreateName(text);
                    if (createError) setCreateError(null);
                  }}
                  placeholder={createPlaceholder ?? 'Nombre'}
                  placeholderTextColor={ticketColors.ink3}
                  autoFocus
                  autoCapitalize="words"
                  onSubmitEditing={() => void submitCreate()}
                  accessibilityLabel={createLabel}
                  testID="master-filter.create-input"
                />
                <TouchableOpacity
                  style={[
                    styles.createConfirmBtn,
                    (!createName.trim() || createBusy) && styles.createConfirmBtnDisabled,
                  ]}
                  onPress={() => void submitCreate()}
                  disabled={!createName.trim() || createBusy}
                  accessibilityRole="button"
                  accessibilityLabel={`Crear ${createLabel}`}
                >
                  <Text style={styles.createConfirmBtnText}>
                    {createBusy ? 'Creando…' : 'Crear'}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => {
                    setCreating(false);
                    setCreateName('');
                    setCreateError(null);
                  }}
                  style={styles.createCancelBtn}
                  accessibilityRole="button"
                  accessibilityLabel="Cancelar creación"
                >
                  <Ionicons name="close" size={16} color={ticketColors.ink3} />
                </TouchableOpacity>
              </View>
            ) : (
              <TouchableOpacity
                style={styles.createTrigger}
                onPress={() => setCreating(true)}
                accessibilityRole="button"
                accessibilityLabel={createLabel}
                testID="master-filter.create-trigger"
              >
                <Ionicons name="add-circle-outline" size={16} color={ticketColors.gold} />
                <Text style={styles.createTriggerText}>{createLabel}</Text>
              </TouchableOpacity>
            )
          ) : null}
          {createError ? (
            <Text style={styles.createErrorText}>{createError}</Text>
          ) : null}

          <ScrollView
            style={[styles.modalSheetScroll, { maxHeight: screenHeight * (ticket ? 0.42 : 0.55) }]}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator
          >
            <View style={[styles.optionsList, ticket && styles.optionsListTicket]}>
              {showAllOption && allOptionLabel && !query.trim() ? (
                <TouchableOpacity
                  style={[
                    ticket ? styles.ticketRow : styles.sheetRow,
                    !selectedId && (ticket ? styles.ticketRowSelected : styles.sheetRowSelected),
                  ]}
                  onPress={() => selectAndClose(null)}
                  accessibilityRole="button"
                  accessibilityState={{ selected: !selectedId }}
                >
                  <Text
                    style={[
                      ticket ? styles.ticketRowTitle : styles.sheetRowTitle,
                      !selectedId &&
                        (ticket ? styles.ticketRowTitleSelected : styles.sheetRowTitleSelected),
                    ]}
                  >
                    {allOptionLabel}
                  </Text>
                  {ticket && !selectedId ? <Text style={styles.ticketCheck}>✓</Text> : null}
                </TouchableOpacity>
              ) : null}
              {filteredOptions.map((option) => {
                const selected = selectedId === option.id;
                return (
                  <TouchableOpacity
                    key={option.id}
                    style={[
                      ticket ? styles.ticketRow : styles.sheetRow,
                      selected && (ticket ? styles.ticketRowSelected : styles.sheetRowSelected),
                      option.disabled && styles.sheetRowDisabled,
                    ]}
                    onPress={() => selectAndClose(option.id)}
                    disabled={option.disabled}
                    accessibilityRole="button"
                    accessibilityState={{ selected, disabled: option.disabled === true }}
                    testID={`master-filter-option.${option.id}`}
                  >
                    {ticket ? (
                      <>
                        <Text
                          style={[styles.ticketRowTitle, selected && styles.ticketRowTitleSelected]}
                          numberOfLines={1}
                        >
                          {option.name}
                          {option.subtitle ? (
                            <Text
                              style={[
                                styles.ticketRowSubtitle,
                                selected && styles.ticketRowSubtitleSelected,
                              ]}
                            >
                              {' '}
                              {option.subtitle}
                            </Text>
                          ) : null}
                        </Text>
                        {selected ? <Text style={styles.ticketCheck}>✓</Text> : null}
                      </>
                    ) : (
                      <View style={styles.sheetRowTextCol}>
                        <Text
                          style={[styles.sheetRowTitle, selected && styles.sheetRowTitleSelected]}
                          numberOfLines={1}
                        >
                          {option.name}
                        </Text>
                        {option.subtitle ? (
                          <Text style={styles.sheetRowSubtitle} numberOfLines={1}>
                            {option.subtitle}
                          </Text>
                        ) : null}
                      </View>
                    )}
                  </TouchableOpacity>
                );
              })}
              {showSearch && filteredOptions.length === 0 ? (
                <Text style={styles.emptySearch}>Sin coincidencias</Text>
              ) : null}
            </View>
          </ScrollView>

          {showFooter ? (
            <View style={[styles.footerRow, ticket && styles.footerRowTicket]}>
              {footerClearLabel && onFooterClear ? (
                <TouchableOpacity
                  style={[styles.footerBtn, styles.footerBtnGhost]}
                  onPress={() => {
                    onFooterClear();
                    onClose();
                  }}
                  accessibilityRole="button"
                  accessibilityLabel={footerClearLabel}
                >
                  <Text style={styles.footerBtnGhostText}>{footerClearLabel}</Text>
                </TouchableOpacity>
              ) : null}
              {footerCreateLabel && onFooterCreate ? (
                <TouchableOpacity
                  style={[styles.footerBtn, styles.footerBtnCreate]}
                  onPress={onFooterCreate}
                  accessibilityRole="button"
                  accessibilityLabel={footerCreateLabel}
                >
                  <Text style={styles.footerBtnCreateText}>{footerCreateLabel}</Text>
                </TouchableOpacity>
              ) : null}
            </View>
          ) : null}
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalBackdrop: {
    flex: 1,
    backgroundColor: ticketColors.dim,
    justifyContent: 'flex-end',
  },
  modalBackdropTicket: {
    backgroundColor: ticketColors.dim,
  },
  modalBackdropDialog: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  modalSheet: {
    backgroundColor: ticketColors.paper,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingTop: 8,
    paddingHorizontal: 20,
    borderTopWidth: 1,
    borderColor: ticketColors.ruleSoft,
    overflow: 'hidden',
    zIndex: 1,
  },
  modalSheetTicket: {
    backgroundColor: ticketColors.paper,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    borderColor: ticketColors.ruleSoft,
    paddingHorizontal: 16,
    paddingTop: 12,
  },
  modalSheetTicketWide: {
    maxWidth: 420,
    width: '100%',
    alignSelf: 'center',
    borderRadius: 16,
    borderWidth: 1,
    borderBottomLeftRadius: 16,
    borderBottomRightRadius: 16,
  },
  grabber: {
    width: 28,
    height: 3,
    borderRadius: 2,
    backgroundColor: ticketColors.ruleSoft,
    alignSelf: 'center',
    marginTop: 2,
    marginBottom: 12,
  },
  grabberTicket: {
    width: 28,
    height: 3,
    marginTop: 2,
    marginBottom: 12,
    backgroundColor: ticketColors.ruleSoft,
  },
  modalSheetHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 8,
    paddingHorizontal: 2,
  },
  modalSheetTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    flex: 1,
    minWidth: 0,
    paddingRight: 12,
  },
  closeBtn: {
    width: 32,
    height: 32,
    borderRadius: 10,
    backgroundColor: ticketColors.paper,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  optionsHint: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '500',
    marginBottom: 10,
    fontFamily: ticketFonts.sans,
  },
  optionsHintTicket: {
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '500',
    color: ticketColors.ink3,
    marginTop: -2,
    marginBottom: 10,
    fontFamily: ticketFonts.sans,
  },
  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    minHeight: 40,
    paddingHorizontal: spacing.sm,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    marginBottom: 12,
  },
  searchWrapTicket: {
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    marginBottom: 8,
    paddingHorizontal: 12,
  },
  searchIcon: {
    marginRight: 2,
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    paddingVertical: 8,
    paddingHorizontal: 0,
    margin: 0,
    borderWidth: 0,
    backgroundColor: 'transparent',
    color: ticketColors.ink,
    fontSize: 15,
    fontFamily: ticketFonts.sans,
  },
  searchInputTicket: {
    fontSize: 14,
    fontWeight: '500',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  clearSearchBtn: {
    padding: 2,
  },
  modalSheetScroll: {
    flexGrow: 0,
    flexShrink: 1,
  },
  optionsList: {
    gap: 8,
    paddingBottom: spacing.md,
  },
  optionsListTicket: {
    gap: 8,
    paddingBottom: spacing.sm,
  },
  emptySearch: {
    ...typography.caption,
    color: ticketColors.ink3,
    textAlign: 'center',
    paddingVertical: spacing.md,
    fontFamily: ticketFonts.sans,
  },
  sheetRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingVertical: 14,
    paddingHorizontal: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  sheetRowSelected: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.gold,
  },
  sheetRowDisabled: {
    opacity: 0.5,
  },
  sheetRowTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  sheetRowTitleSelected: {
    color: ticketColors.goldInk,
    fontWeight: '700',
    fontFamily: ticketFonts.sans,
  },
  sheetRowTextCol: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  sheetRowSubtitle: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
  ticketRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.tile,
  },
  ticketRowSelected: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
  },
  ticketRowTitle: {
    flex: 1,
    minWidth: 0,
    fontSize: 14,
    fontWeight: '600',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  ticketRowTitleSelected: {
    color: ticketColors.brandInk,
    fontWeight: '700',
  },
  ticketRowSubtitle: {
    fontSize: 13,
    fontWeight: '500',
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
  ticketRowSubtitleSelected: {
    color: ticketColors.brandInk,
    opacity: 0.85,
  },
  ticketCheck: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.brandInk,
    fontFamily: ticketFonts.sans,
  },
  createTrigger: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: ticketColors.ruleSoft,
    marginBottom: 10,
  },
  createTriggerText: {
    fontSize: 14,
    fontWeight: '600',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  createRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
  },
  createInput: {
    flex: 1,
    minWidth: 0,
    minHeight: 40,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.paper,
    color: ticketColors.ink,
    fontSize: 14,
    fontFamily: ticketFonts.sans,
  },
  createConfirmBtn: {
    minHeight: 40,
    paddingHorizontal: 14,
    borderRadius: 10,
    backgroundColor: ticketColors.gold,
    alignItems: 'center',
    justifyContent: 'center',
  },
  createConfirmBtnDisabled: {
    opacity: 0.45,
  },
  createConfirmBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.goldInk,
    fontFamily: ticketFonts.sans,
  },
  createCancelBtn: {
    width: 36,
    height: 40,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  createErrorText: {
    fontSize: 12,
    fontWeight: '500',
    color: ticketColors.bad,
    marginTop: -4,
    marginBottom: 8,
    fontFamily: ticketFonts.sans,
  },
  footerRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 12,
  },
  footerRowTicket: {
    marginTop: 10,
    paddingTop: 10,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.ruleSoft,
  },
  footerBtn: {
    flex: 1,
    minHeight: 44,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 14,
  },
  footerBtnGhost: {
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.tile,
  },
  footerBtnGhostText: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  /** Outline secondary CTA — clear on paper; avoids washed gold fill. */
  footerBtnCreate: {
    borderWidth: 1.5,
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.paper,
  },
  footerBtnCreateText: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.gold,
    fontFamily: ticketFonts.sans,
  },
});
